import { useState } from "react";
import { Copy, Smartphone, Zap } from "lucide-react";
import { useAppConfig } from "@/lib/appConfig";
import { copyText } from "@/components/FloatingMic";

const ONE_TAP = ["Tap \"Add Mulankan Shortcut\" above, then tap Add Shortcut."];
const SHORTCUT = [
  "Open the Shortcuts app and tap + to make a new shortcut.",
  "Add the action \"Open URLs\" and paste the link below.",
  "Name it \"Mulankan\" and tap Done.",
];
export const GUIDES = {
  backtap: { label: "iPhone · Back Tap", ios: true, steps: [
    "Go to Settings → Accessibility → Touch → Back Tap → Double Tap and choose \"Mulankan\". iPhone 15 Pro and newer can also put it on the Action button (Settings → Action Button → Shortcut).",
    "While typing in WhatsApp, Gmail or Notes, double-tap the back of your phone, speak, tap ✓, then paste."] },
  control: { label: "iPhone · Control Center", ios: true, steps: [
    "Swipe down from the top-right corner, tap + → Add a Control → Shortcuts, and pick \"Mulankan\". (iOS 18 or newer)",
    "For the Lock Screen: long-press the Lock Screen → Customize → Lock Screen → tap a bottom button → Shortcut → \"Mulankan\".",
    "While typing in any app, swipe down, tap the Mulankan button, speak, tap ✓, then paste."] },
  android: { label: "Android · Home screen", steps: [
    "Install Mulankan to your home screen (see Phone app).",
    "Long-press the Mulankan icon and drag \"Quick listen\" onto your home screen — or open the link below in Chrome, tap ⋮ → Add to Home screen.",
    "While typing in any app, tap Quick listen, speak, tap ✓, then paste."] },
};

export function AddShortcutButton({ testId = "add-shortcut-button", className = "" }) {
  const { ios_shortcut_url: url } = useAppConfig();
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={testId} className={`btn-accent justify-center ${className}`}>
      <Zap size={15} /> Add Mulankan Shortcut
    </a>
  );
}

export function GuideSteps({ testId = "any-app" }) {
  const [tab, setTab] = useState(/Android/i.test(navigator.userAgent) ? "android" : "backtap");
  const { ios_shortcut_url: url } = useAppConfig();
  const link = `${window.location.origin}/listen`;
  const g = GUIDES[tab];
  const steps = g.ios ? [...(url ? ONE_TAP : SHORTCUT), ...g.steps] : g.steps;
  return (
    <>
      <div className="flex flex-wrap gap-2 mb-5">
        {Object.entries(GUIDES).map(([id, g]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`${testId}-tab-${id}`} className={`pill text-xs ${tab === id ? "pill-on" : ""}`}>{g.label}</button>
        ))}
      </div>
      {g.ios && url && <AddShortcutButton testId={`${testId}-add-shortcut`} className="w-full mb-5" />}
      <ol className="space-y-2" data-testid={`${testId}-${tab}-steps`}>
        {steps.map((t, i) => (
          <li key={i} className="flex gap-3 text-sm text-white/70"><span className="w-5 h-5 shrink-0 rounded-full bg-white/[0.06] text-[11px] flex items-center justify-center text-white/60">{i + 1}</span>{t}</li>
        ))}
      </ol>
      {(!g.ios || !url) && <div className="flex gap-2 items-center mt-5">
        <code className="flex-1 min-w-0 truncate rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2.5 text-xs text-[var(--ink)]" data-testid={`${testId}-link`}>{link}</code>
        <button onClick={() => copyText(link)} data-testid={`${testId}-copy-link`} className="btn-accent text-xs shrink-0"><Copy size={13} /> Copy link</button>
      </div>}
    </>
  );
}

export function AnyAppSection() {
  return (
    <section id="any-app" className="mb-10 scroll-mt-24" data-testid="any-app-section">
      <div className="eyebrow mb-4">Use while typing in any app</div>
      <div className="panel p-6 max-w-3xl">
        <div className="flex items-start gap-3 mb-5">
          <Smartphone size={20} className="text-[var(--accent)] mt-0.5 shrink-0" />
          <p className="text-sm text-white/60">Phone keyboards can't include Mulankan, so add a one-tap button instead. It opens the listening screen, polishes your words and copies them, ready to paste.</p>
        </div>
        <GuideSteps />
        <a href="/download" data-testid="settings-keyboard-app-link" className="mt-5 flex items-center justify-between gap-3 rounded-2xl border border-[var(--accent)]/30 bg-[var(--accent)]/[0.06] px-4 py-3 text-sm text-[var(--ink)] hover:border-[var(--accent)]/60 transition-colors"><span><b>Get the Mulankan app</b> — Android download, iPhone home screen, keyboard coming soon</span><span className="text-[var(--accent)] shrink-0">Get it →</span></a>
      </div>
    </section>
  );
}
